import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useOfflineSync } from './hooks/useOfflineSync';
import PhotoCapture from './components/PhotoCapture';
import OfflineSyncBanner from './components/OfflineSyncBanner';
import SignatureCapture from './components/SignatureCapture';
import GpsTracker from './components/GpsTracker';

const MobileTechnicianApp = () => {
  const [activeTab, setActiveTab] = useState('today');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [techNote, setTechNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [statuses, setStatuses] = useState([]);

  const {
    isOnline,
    pendingCount,
    syncing,
    lastSyncTime,
    queueAction,
    syncPendingActions,
    cacheJobs,
    getCachedJobs,
    savePhotoLocally,
    getLocalPhotos
  } = useOfflineSync();

  useEffect(() => {
    fetchJobs();
    fetchStatuses();
  }, [activeTab, isOnline]);

  const fetchStatuses = async () => {
    try {
      if (isOnline) {
        const { data } = await supabase?.from('ticket_statuses')?.select('id, name, color');
        setStatuses(data || []);
      }
    } catch (err) {
      console.error('Error fetching statuses:', err?.message);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      if (!isOnline) {
        // Load from cache
        const cached = await getCachedJobs();
        setJobs(cached || []);
        setLoading(false);
        return;
      }

      const today = new Date();
      const todayStr = today?.toISOString()?.split('T')?.[0];
      const weekEnd = new Date(today);
      weekEnd?.setDate(weekEnd?.getDate() + 7);
      const weekEndStr = weekEnd?.toISOString()?.split('T')?.[0];

      let query = supabase?.from('tickets')?.select(`
        id, ticket_number, description, scheduled_date, estimated_duration,
        notes, created_at,
        customer:customers(name, phone, email, address),
        status:ticket_statuses(id, name, color),
        priority:ticket_priorities(name, color),
        service_type:ticket_service_types(name),
        ticket_technicians(technician:personnel(name, email)),
        ticket_products(id, name, quantity, price)
      `);

      if (activeTab === 'today') {
        query = query?.gte('scheduled_date', todayStr + 'T00:00:00')?.lte('scheduled_date', todayStr + 'T23:59:59');
      } else if (activeTab === 'upcoming') {
        query = query?.gt('scheduled_date', todayStr + 'T23:59:59')?.lte('scheduled_date', weekEndStr + 'T23:59:59');
      } else {
        query = query?.in('status_id',
          statuses?.filter(s => ['in progress', 'open']?.includes(s?.name?.toLowerCase()))?.map(s => s?.id) || []
        );
      }

      const { data, error } = await query?.order('scheduled_date', { ascending: true });
      if (error) throw error;
      const jobData = data || [];
      setJobs(jobData);
      // Cache jobs for offline use
      await cacheJobs(jobData);
    } catch (err) {
      console.error('Error fetching jobs:', err?.message);
      // Fallback to cache on error
      const cached = await getCachedJobs();
      setJobs(cached || []);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (job) => {
    setCheckingIn(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const inProgressStatus = statuses?.find(s => s?.name?.toLowerCase() === 'in progress');
      if (!inProgressStatus) throw new Error('In Progress status not found');

      const updatedNotes = job?.notes
        ? `${job?.notes}\n\n[Technician Checked In: ${new Date()?.toLocaleString()}]`
        : `[Technician Checked In: ${new Date()?.toLocaleString()}]`;

      if (isOnline) {
        const { error } = await supabase?.from('tickets')?.update({
          status_id: inProgressStatus?.id,
          notes: updatedNotes
        })?.eq('id', job?.id);

        if (error) throw error;
      } else {
        await queueAction('CHECK_IN', {
          ticketId: job?.id,
          statusId: inProgressStatus?.id,
          notes: updatedNotes
        });
      }

      setSuccessMsg(isOnline ? 'Checked in successfully!' : 'Check-in saved offline. Will sync when connected.');
      setSelectedJob(prev => prev ? { ...prev, status: inProgressStatus } : prev);
      setJobs(prev => prev?.map(j => j?.id === job?.id ? { ...j, status: inProgressStatus } : j));
    } catch (err) {
      setErrorMsg('Failed to check in. Please try again.');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleStatusUpdate = async (job, statusId) => {
    setUpdatingStatus(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      if (isOnline) {
        const { error } = await supabase?.from('tickets')?.update({ status_id: statusId })?.eq('id', job?.id);
        if (error) throw error;
      } else {
        await queueAction('UPDATE_STATUS', { ticketId: job?.id, statusId });
      }
      const newStatus = statuses?.find(s => s?.id === statusId);
      setSuccessMsg(isOnline ? `Status updated to ${newStatus?.name}` : `Status queued for sync`);
      setSelectedJob(prev => prev ? { ...prev, status: newStatus } : prev);
      setJobs(prev => prev?.map(j => j?.id === job?.id ? { ...j, status: newStatus } : j));
    } catch (err) {
      setErrorMsg('Failed to update status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSaveNote = async (job) => {
    if (!techNote?.trim()) return;
    setSavingNote(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const updatedNotes = job?.notes
        ? `${job?.notes}\n\n[${new Date()?.toLocaleString()}] ${techNote?.trim()}`
        : `[${new Date()?.toLocaleString()}] ${techNote?.trim()}`;

      if (isOnline) {
        const { error } = await supabase?.from('tickets')?.update({ notes: updatedNotes })?.eq('id', job?.id);
        if (error) throw error;
      } else {
        await queueAction('SAVE_NOTE', { ticketId: job?.id, notes: updatedNotes });
      }
      setSuccessMsg(isOnline ? 'Note saved successfully!' : 'Note saved offline. Will sync when connected.');
      setTechNote('');
      setSelectedJob(prev => prev ? { ...prev, notes: updatedNotes } : prev);
      setJobs(prev => prev?.map(j => j?.id === job?.id ? { ...j, notes: updatedNotes } : j));
    } catch (err) {
      setErrorMsg('Failed to save note.');
    } finally {
      setSavingNote(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not Scheduled';
    return new Date(dateStr)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getPriorityColor = (priority) => {
    const p = priority?.name?.toLowerCase();
    if (p === 'critical' || p === 'high') return 'bg-red-100 text-red-700 border-red-200';
    if (p === 'medium') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  const getStatusColor = (status) => {
    const s = status?.name?.toLowerCase();
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'in progress') return 'bg-blue-100 text-blue-700';
    if (s === 'open') return 'bg-gray-100 text-gray-600';
    if (s === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-600';
  };

  const canCheckIn = (job) => {
    const s = job?.status?.name?.toLowerCase();
    return s === 'open' || s === 'scheduled' || s === 'pending';
  };

  const tabs = [
    { id: 'today', label: "Today\'s Jobs", icon: 'Calendar' },
    { id: 'upcoming', label: 'Upcoming', icon: 'Clock' },
    { id: 'active', label: 'Active', icon: 'Zap' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Mobile Technician App - RIGJOCKEY</title>
      </Helmet>
      <Header />
      <main className="pt-4 pb-20 px-4 max-w-2xl mx-auto">
        {/* Page Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Smartphone" size={22} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-xl font-heading font-semibold text-foreground">Technician App</h1>
                <p className="text-sm text-muted-foreground">Manage your field jobs</p>
              </div>
            </div>
            {/* Online/Offline indicator */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
              isOnline ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                isOnline ? 'bg-green-500' : 'bg-yellow-500'
              }`} />
              {isOnline ? 'Online' : 'Offline'}
            </div>
          </div>
        </div>

        {/* Offline Sync Banner */}
        <OfflineSyncBanner
          isOnline={isOnline}
          pendingCount={pendingCount}
          syncing={syncing}
          lastSyncTime={lastSyncTime}
          onManualSync={syncPendingActions}
        />

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 mb-6">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => { setActiveTab(tab?.id); setSelectedJob(null); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab?.id
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={14} color="currentColor" />
              <span className="hidden sm:inline">{tab?.label}</span>
              <span className="sm:hidden">{tab?.id === 'today' ? 'Today' : tab?.id === 'upcoming' ? 'Soon' : 'Active'}</span>
            </button>
          ))}
        </div>

        {/* Job List */}
        {!selectedJob ? (
          <div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">{isOnline ? 'Loading jobs...' : 'Loading cached jobs...'}</p>
              </div>
            ) : jobs?.length === 0 ? (
              <div className="bg-card rounded-2xl border border-border p-10 text-center">
                <Icon name="CheckCircle" size={40} color="#9ca3af" />
                <p className="mt-3 font-medium text-foreground">No jobs for this period</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {isOnline ? 'Check back later or view other tabs' : 'No cached jobs available offline'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {jobs?.map(job => (
                  <button
                    key={job?.id}
                    onClick={() => { setSelectedJob(job); setSuccessMsg(''); setErrorMsg(''); setTechNote(''); }}
                    className="w-full text-left bg-card rounded-2xl border border-border p-4 hover:shadow-md transition-all active:scale-[0.99]"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">
                          {job?.ticket_number}
                        </span>
                        {job?.priority && (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${getPriorityColor(job?.priority)}`}>
                            {job?.priority?.name}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(job?.status)}`}>
                        {job?.status?.name || 'Unknown'}
                      </span>
                    </div>

                    <p className="font-semibold text-foreground text-sm mb-1">{job?.customer?.name}</p>
                    {job?.service_type && (
                      <p className="text-xs text-muted-foreground mb-2">{job?.service_type?.name}</p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Icon name="Clock" size={12} color="currentColor" />
                        {formatDate(job?.scheduled_date)}
                      </div>
                      {job?.customer?.address && (
                        <div className="flex items-center gap-1 truncate">
                          <Icon name="MapPin" size={12} color="currentColor" />
                          <span className="truncate">{job?.customer?.address}</span>
                        </div>
                      )}
                    </div>

                    {canCheckIn(job) && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-primary font-medium">
                        <Icon name="LogIn" size={12} color="currentColor" />
                        Ready to check in
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Job Detail View */
          (<div>
            <button
              onClick={() => { setSelectedJob(null); setSuccessMsg(''); setErrorMsg(''); }}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <Icon name="ArrowLeft" size={16} color="currentColor" />
              Back to jobs
            </button>
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              {/* Job Header */}
              <div className="p-5 border-b border-border">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <span className="text-xs font-mono font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">
                      {selectedJob?.ticket_number}
                    </span>
                    <h2 className="text-lg font-heading font-semibold text-foreground mt-2">
                      {selectedJob?.customer?.name}
                    </h2>
                    {selectedJob?.service_type && (
                      <p className="text-sm text-muted-foreground">{selectedJob?.service_type?.name}</p>
                    )}
                  </div>
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${getStatusColor(selectedJob?.status)}`}>
                    {selectedJob?.status?.name}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Clock" size={16} color="#6b7280" />
                      <span className="text-muted-foreground">Scheduled:</span>
                      <span className="font-medium text-foreground">{formatDate(selectedJob?.scheduled_date)}</span>
                    </div>
                    {selectedJob?.estimated_duration && (
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="Timer" size={16} color="#6b7280" />
                        <span className="text-muted-foreground">Duration:</span>
                        <span className="font-medium text-foreground">{selectedJob?.estimated_duration} hrs</span>
                      </div>
                    )}
                    {selectedJob?.customer?.address && (
                      <div className="flex items-start gap-2 text-sm">
                        <Icon name="MapPin" size={16} color="#6b7280" />
                        <div>
                          <span className="text-muted-foreground">Location:</span>
                          <p className="font-medium text-foreground">{selectedJob?.customer?.address}</p>
                        </div>
                      </div>
                    )}
                    {selectedJob?.customer?.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="Phone" size={16} color="#6b7280" />
                        <span className="text-muted-foreground">Contact:</span>
                        <a href={`tel:${selectedJob?.customer?.phone}`} className="font-medium text-primary">
                          {selectedJob?.customer?.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedJob?.description && (
                <div className="p-5 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground mb-2">Job Description</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJob?.description}</p>
                </div>
              )}

              {/* Parts / Products */}
              {selectedJob?.ticket_products?.length > 0 && (
                <div className="p-5 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Parts & Materials</h3>
                  <div className="space-y-2">
                    {selectedJob?.ticket_products?.map(p => (
                      <div key={p?.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-foreground">{p?.name}</p>
                          <p className="text-xs text-muted-foreground">Qty: {p?.quantity}</p>
                        </div>
                        {p?.price && (
                          <p className="text-sm font-semibold text-foreground">
                            ${(Number(p?.price) * Number(p?.quantity || 1))?.toFixed(2)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Photo Capture Section */}
              <PhotoCapture
                ticketId={selectedJob?.id}
                ticketNumber={selectedJob?.ticket_number}
                isOnline={isOnline}
                savePhotoLocally={savePhotoLocally}
                getLocalPhotos={getLocalPhotos}
                queueAction={queueAction}
                onPhotoSaved={(message) => {
                  setSuccessMsg(message);
                  setTimeout(() => setSuccessMsg(''), 3000);
                }}
              />

              {/* GPS Location Tracking */}
              <GpsTracker
                ticketId={selectedJob?.id}
                isOnline={isOnline}
                queueAction={queueAction}
              />

              {/* Signature Capture */}
              <SignatureCapture
                ticketId={selectedJob?.id}
                ticketNumber={selectedJob?.ticket_number}
                isOnline={isOnline}
                queueAction={queueAction}
                onSignatureSaved={(message) => {
                  setSuccessMsg(message);
                  setTimeout(() => setSuccessMsg(''), 3000);
                }}
              />

              {/* Technician Notes */}
              <div className="p-5 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3">Field Notes</h3>
                {selectedJob?.notes && (
                  <div className="bg-muted rounded-lg p-3 mb-3">
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{selectedJob?.notes}</p>
                  </div>
                )}
                <textarea
                  value={techNote}
                  onChange={(e) => setTechNote(e?.target?.value)}
                  placeholder="Add field notes, observations, or updates..."
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-xl text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                />
                <button
                  onClick={() => handleSaveNote(selectedJob)}
                  disabled={savingNote || !techNote?.trim()}
                  className="mt-2 w-full py-2.5 px-4 bg-muted text-foreground rounded-xl text-sm font-medium hover:bg-muted/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingNote ? (
                    <><div className="w-4 h-4 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" /> Saving...</>
                  ) : (
                    <><Icon name="Save" size={14} color="currentColor" /> {isOnline ? 'Save Note' : 'Save Note (Offline)'}</>
                  )}
                </button>
              </div>

              {/* Status Update */}
              <div className="p-5 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3">Update Status</h3>
                {!isOnline && (
                  <p className="text-xs text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-1.5">
                    <Icon name="WifiOff" size={12} color="currentColor" />
                    Status changes will sync when you're back online
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {statuses?.filter(s => !['draft']?.includes(s?.name?.toLowerCase()))?.map(status => (
                    <button
                      key={status?.id}
                      onClick={() => handleStatusUpdate(selectedJob, status?.id)}
                      disabled={updatingStatus || selectedJob?.status?.id === status?.id}
                      className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all border ${
                        selectedJob?.status?.id === status?.id
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-card text-foreground border-border hover:bg-muted disabled:opacity-50'
                      }`}
                    >
                      {status?.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="p-5">
                {successMsg && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl mb-3">
                    <Icon name="CheckCircle" size={16} color="#16a34a" />
                    <p className="text-sm text-green-700">{successMsg}</p>
                  </div>
                )}
                {errorMsg && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-3">
                    <Icon name="AlertCircle" size={16} color="#ef4444" />
                    <p className="text-sm text-red-700">{errorMsg}</p>
                  </div>
                )}

                {canCheckIn(selectedJob) && (
                  <button
                    onClick={() => handleCheckIn(selectedJob)}
                    disabled={checkingIn}
                    className="w-full py-3.5 px-4 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2 mb-3"
                  >
                    {checkingIn ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Checking In...</>
                    ) : (
                      <><Icon name="LogIn" size={18} color="currentColor" /> Check In to Job</>
                    )}
                  </button>
                )}

                {selectedJob?.customer?.address && (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(selectedJob?.customer?.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 px-4 bg-card border border-border text-foreground rounded-xl font-medium text-sm hover:bg-muted transition-colors flex items-center justify-center gap-2"
                  >
                    <Icon name="Navigation" size={16} color="currentColor" />
                    Get Directions
                  </a>
                )}
              </div>
            </div>
          </div>)
        )}
      </main>
    </div>
  );
};

export default MobileTechnicianApp;
